import { SELECTORS, CLASS_NAMES, EVENTS, MISC } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Footer module
 */
export default class Footer extends MessageBus {
  /**
   * Constructor function of the Footer class. Must receive one argument, a DOM
   * node representing the footer element.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {Footer} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {Footer} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.toggles = this.element.querySelectorAll(SELECTORS.ACCORDION_TOGGLE);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {Footer} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleToggleClick = this.onToggleClick.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with view
   * @returns {Footer} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.toggles.forEach((toggle) => {
      toggle.addEventListener(EVENTS.CLICK, this.handleToggleClick);
    });

    return this;
  }

  /**
   * Handles accordion toggle click. Does nothing on tablet and above.
   * Toggles aria-expanded on the button and the open class on the body.
   * @param {Event} event - the click event object
   * @returns {Footer} A reference to the current instance of the class
   */
  onToggleClick(event) {
    const isTablet = window.matchMedia(SELECTORS.MIN_TABLET).matches;

    if (isTablet) return this;

    const toggle = event.currentTarget;
    const isExpanded =
      toggle.getAttribute(SELECTORS.ARIA_EXPANDED) === MISC.TRUE;

    // Close all other open toggles
    this.toggles.forEach((otherToggle) => {
      if (otherToggle === toggle) return;

      otherToggle.setAttribute(SELECTORS.ARIA_EXPANDED, MISC.FALSE);

      const otherBodyId = otherToggle.getAttribute(SELECTORS.ARIA_CONTROLS);
      const otherBody = document.getElementById(otherBodyId);

      otherBody?.classList.remove(CLASS_NAMES.ACCORDION_BODY_OPEN);
    });

    // Toggle the clicked one
    toggle.setAttribute(SELECTORS.ARIA_EXPANDED, String(!isExpanded));

    const bodyId = toggle.getAttribute(SELECTORS.ARIA_CONTROLS);
    const body = document.getElementById(bodyId);

    body?.classList.toggle(CLASS_NAMES.ACCORDION_BODY_OPEN, !isExpanded);

    return this;
  }
}
